import React, { useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle, MapPin, Shield, Users, FileText } from 'lucide-react';

const NonDisclosureStates = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = 'Non Disclosure States | Property Tax Appeals & Assessor Protest Guide';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Learn how non-disclosure states affect property tax appeals and assessor protests. Discover strategies for property tax protests in states where sale prices are not public.'
      );
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Learn how non-disclosure states affect property tax appeals and assessor protests. Discover strategies for property tax protests in states where sale prices are not public.';
      document.head.appendChild(meta);
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute(
        'content',
        'non disclosure state, property tax appeals, assessor protest, property tax protest, non-disclosure states, property valuation, comparable sales, MLS data, property assessment'
      );
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'non disclosure state, property tax appeals, assessor protest, property tax protest, non-disclosure states, property valuation, comparable sales, MLS data, property assessment';
      document.head.appendChild(meta);
    }
  }, []);

  const nonDisclosureStates = [
    {
      name: 'Alaska',
      description: 'Sale prices are not recorded publicly. Property condition disclosures still apply, and market insights typically come from professionals with MLS access.'
    },
    {
      name: 'Idaho',
      description: 'Sale prices remain private unless voluntarily shared. Real estate agents and appraisers rely heavily on MLS data to evaluate market value.'
    },
    {
      name: 'Kansas',
      description: 'Public records do not include sale prices. MLS systems and local assessor data are often the primary sources for valuation research.'
    },
    {
      name: 'Louisiana',
      description: 'A non-disclosure state where local MLS systems play a critical role in understanding market activity and comparable sales.'
    },
    {
      name: 'Mississippi',
      description: 'Deeds commonly list nominal amounts rather than true sale prices. Reliable valuation data generally requires assistance from a licensed real estate professional.'
    },
    {
      name: 'Missouri',
      description: 'Some counties require disclosure, while others do not. Many areas rely on MLS data to supplement public records.',
      partial: true
    },
    {
      name: 'Montana',
      description: 'Sale prices are not publicly accessible. County assessments depend on indirect methods and private market data.'
    },
    {
      name: 'New Mexico',
      description: 'Sale prices are kept confidential and generally available only to the property owner or through licensed professionals.'
    },
    {
      name: 'North Dakota',
      description: 'Public sale price data is limited. MLS access is often necessary to obtain accurate comparable sales.'
    },
    {
      name: 'Texas',
      description: 'One of the largest non-disclosure states. Counties estimate value using modeling and trends, while MLS data remains the most reliable source for actual sale prices.'
    },
    {
      name: 'Utah',
      description: 'Sale prices are not publicly disclosed, and additional privacy measures further restrict access to transaction data.'
    },
    {
      name: 'Wyoming',
      description: 'While sale prices are not public records, MLS systems are commonly used by professionals to track market activity.'
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-tax-deepBlue via-tax-brightBlue to-tax-deepBlue text-white py-16 md:py-24">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium">Understanding Property Tax Appeals</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-6 text-white">
              Non-Disclosure States and How They Affect Your Property Value & Tax Protest
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
              Learn how non-disclosure states impact property tax assessments and what you can do to challenge unfair valuations.
            </p>
          </div>
        </div>
      </section>

      <div className="container py-12">
        {/* Introduction */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="border-tax-borderGray">
            <CardContent className="p-6 md:p-8">
              <p className="text-lg text-tax-neutralGray leading-relaxed">
                When counties assess your property for tax purposes, they aim to estimate fair market value—what your home would realistically sell for in an open market. One of the strongest indicators of that value is recent comparable sales.
              </p>
              <p className="text-lg text-tax-neutralGray leading-relaxed mt-4">
                However, in certain states, sale prices are intentionally kept out of public records. These are known as <strong className="text-tax-deepBlue">non-disclosure states</strong>, and they can significantly affect both how counties value property and how homeowners challenge those valuations.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* What Is a Non-Disclosure State */}
        <div className="max-w-4xl mx-auto mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-tax-deepBlue mb-6">
            What Is a Non-Disclosure State?
          </h2>
          <Card className="border-tax-borderGray">
            <CardContent className="p-6 md:p-8">
              <p className="text-tax-neutralGray leading-relaxed mb-6">
                In a non-disclosure state, real estate sale prices are not required to be publicly recorded. Unlike disclosure states—where sold prices appear in county records—these states limit access to that information to protect buyer and seller privacy.
              </p>
              <p className="text-tax-neutralGray leading-relaxed mb-6">
                While privacy is the goal, the trade-off is reduced transparency, which can make it harder for homeowners to:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-warning mt-0.5 flex-shrink-0" />
                  <span className="text-tax-neutralGray">Understand how their assessed value was calculated</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-warning mt-0.5 flex-shrink-0" />
                  <span className="text-tax-neutralGray">Confirm whether their property is over-assessed</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-warning mt-0.5 flex-shrink-0" />
                  <span className="text-tax-neutralGray">Present strong evidence during a property tax protest</span>
                </li>
              </ul>
              <div className="bg-tax-lightGray rounded-lg p-4 border border-tax-borderGray">
                <p className="text-tax-deepBlue font-semibold text-center">
                  There are currently 12 non-disclosure states in the U.S.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* How Counties Value Property */}
        <div className="max-w-4xl mx-auto mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-tax-deepBlue mb-6">
            How Counties Value Property in Non-Disclosure States
          </h2>
          <Card className="border-tax-borderGray">
            <CardContent className="p-6 md:p-8">
              <p className="text-tax-neutralGray leading-relaxed mb-6">
                Without public sales data, county appraisal offices often rely on:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-tax-brightBlue mt-2 flex-shrink-0" />
                  <span className="text-tax-neutralGray">Statistical models and mass appraisal techniques</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-tax-brightBlue mt-2 flex-shrink-0" />
                  <span className="text-tax-neutralGray">Market trends rather than confirmed sale prices</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-tax-brightBlue mt-2 flex-shrink-0" />
                  <span className="text-tax-neutralGray">Limited sales voluntarily shared or estimated</span>
                </li>
              </ul>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-gray-700">
                  <strong>Important:</strong> As a result, assessments in non-disclosure states can sometimes be less precise, increasing the likelihood that a property may be assessed above true market value.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Non-Disclosure States List */}
        <div className="max-w-4xl mx-auto mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-tax-deepBlue mb-6">
            Non-Disclosure States in the U.S.
          </h2>
          <p className="text-tax-neutralGray mb-8">
            Below is an overview of the states where real estate sale prices are not publicly disclosed, along with how market data is typically accessed.
          </p>
          <div className="grid gap-4">
            {nonDisclosureStates.map((state) => (
              <Card key={state.name} className="border-tax-borderGray hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-tax-lightGray flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-tax-deepBlue" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-tax-deepBlue mb-2">
                        {state.name}
                        {state.partial && (
                          <span className="ml-2 text-sm font-normal text-warning">(Partial)</span>
                        )}
                      </h3>
                      <p className="text-tax-neutralGray">{state.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* What This Means for Property Tax Protests */}
        <div className="max-w-4xl mx-auto mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-tax-deepBlue mb-6">
            What This Means for Property Tax Protests
          </h2>
          <Card className="border-tax-borderGray">
            <CardContent className="p-6 md:p-8">
              <p className="text-tax-neutralGray leading-relaxed mb-6">
                In order to protest a property tax assessment, homeowners typically need comparable sales evidence. In non-disclosure states, the challenge isn't that the data doesn't exist—it's that it isn't public.
              </p>
              <p className="text-tax-neutralGray leading-relaxed mb-4">Without sold prices:</p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                  <span className="text-tax-neutralGray">Homeowners may feel stuck or under-prepared</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                  <span className="text-tax-neutralGray">Protest cases can appear weaker without professional support</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                  <span className="text-tax-neutralGray">Counties may rely more heavily on assumptions than real sales</span>
                </li>
              </ul>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-gray-800 font-medium text-center">
                  This is where the right tools and partnerships make all the difference.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* How PropertyTaxHero Helps */}
        <div className="max-w-4xl mx-auto mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-tax-deepBlue mb-6">
            How PropertyTaxHero Helps in Non-Disclosure States
          </h2>
          <Card className="border-tax-borderGray bg-gradient-to-br from-white to-tax-lightGray">
            <CardContent className="p-6 md:p-8">
              <p className="text-lg text-tax-neutralGray leading-relaxed mb-6">
                <strong className="text-tax-deepBlue">PropertyTaxHero.com is 100% free to use</strong>, and it's designed specifically to work with the realities of non-disclosure states—not against them.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-tax-neutralGray">You can generate a complete property tax protest report, even if sold prices aren't immediately available</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-tax-neutralGray">The analysis, adjustments, and valuation logic remain fully valid</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-tax-neutralGray">A real estate agent can easily add confidential MLS sold prices to complete the report</span>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 border border-tax-borderGray">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-6 h-6 text-tax-brightBlue" />
                  <h3 className="text-lg font-semibold text-tax-deepBlue">
                    ✅ All real estate agents can create a free PropertyTaxHero account
                  </h3>
                </div>
                <p className="text-tax-neutralGray mb-4">Agents can:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-tax-brightBlue mt-2 flex-shrink-0" />
                    <span className="text-tax-neutralGray">Access sold prices through their private MLS</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-tax-brightBlue mt-2 flex-shrink-0" />
                    <span className="text-tax-neutralGray">Enter that information securely into PropertyTaxHero</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-tax-brightBlue mt-2 flex-shrink-0" />
                    <span className="text-tax-neutralGray">Help their clients finalize a strong, accurate protest report—at no cost</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* The Bottom Line */}
        <div className="max-w-4xl mx-auto mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-tax-deepBlue mb-6">
            The Bottom Line
          </h2>
          <Card className="border-tax-brightBlue border-2">
            <CardContent className="p-6 md:p-8">
              <p className="text-lg text-tax-neutralGray leading-relaxed mb-4">
                Non-disclosure states may limit public access to sale prices, but they <strong className="text-tax-deepBlue">do not eliminate your ability to challenge an unfair property tax assessment</strong>.
              </p>
              <p className="text-lg text-tax-neutralGray leading-relaxed mb-4">
                Even if comparable sale prices aren't instantly visible, your PropertyTaxHero report is still legitimate and actionable. With the help of a real estate professional, those final details can be added quickly—allowing you to move forward with confidence.
              </p>
              <p className="text-xl font-semibold text-tax-deepBlue text-center mt-6">
                PropertyTaxHero makes property tax protests simpler, fairer, and free—for homeowners and real estate agents alike.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-r from-tax-deepBlue to-tax-brightBlue text-white">
            <CardContent className="p-8 md:p-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-90" />
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white">
                Ready to Start Your Property Tax Protest?
              </h2>
              <p className="text-lg text-white/90 mb-6 max-w-2xl mx-auto">
                Generate your free property tax protest report today—even in a non-disclosure state.
              </p>
              <Button
                asChild
                size="lg"
                className="bg-white text-tax-deepBlue hover:bg-white/90 font-semibold"
              >
                <Link to="/#property-form">Get Your Free Report</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default NonDisclosureStates;
